import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Flag from "@mui/icons-material/Flag";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  styled,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useTaskCalendarSchedule } from "~hooks/useTaskCalendarSchedule";
import { Route } from "~services/router/Route";

import { PageHeader } from "../../atoms/PageHeader/PageHeader";
import { type CarouselTaskItem } from "../TaskDashboardTemplate/taskUtils";
import {
  buildNormalizedDisplayDueByTaskId,
  type DaySchedulePrefs,
  getHourRowHeightsPx,
  getNonWorkingMinuteIntervals,
  minuteOfDayToYpx,
  minuteSpanToPercentOfDay,
  minutesToTimeInputValue,
  sanitizeSchedulePrefs,
  TASK_CALENDAR_SCHEDULE_DAY_LABELS,
  type TaskCalendarSchedulePrefs,
  timeInputValueToMinutes,
  totalDayGridHeightPx,
} from "./taskCalendarNormalize";
import {
  buildTimezoneLegendItems,
  CALENDAR_WEEK_DAY_COUNT,
  calendarAccentColorForTask,
  calendarPriorityAriaLabel,
  calendarPriorityFlagColor,
  type CalendarView,
  DEFAULT_TASK_DURATION_MINUTES,
  formatHourLabel,
  getCalendarDueForDisplay,
  getCalendarWeekDaysSunSat,
  getMonthGridCells,
  getUndatedTasks,
  HOUR_ROW_PX,
  hoursInDay,
  isUrgentOrHighCalendarPriority,
  taskDueDateTime,
  tasksByLocalDateKey,
  timedTasksForDay,
} from "./taskCalendarUtils";

export interface TaskCalendarTemplateProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  selectedDate: DateTime;
  onSelectedDateChange: (d: DateTime) => void;
  tasks: CarouselTaskItem[];
  /** When set, tasks with a `prospectId` open this instead of the task detail dialog. */
  onOpenProspectFromTask?: (task: CarouselTaskItem) => void;
}

const Shell = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  boxSizing: "border-box",
  maxWidth: "100%",
  minHeight: 0,
  minWidth: 0,
  padding: theme.spacing(2),
}));

const CalendarShell = styled(Paper)(() => ({
  flex: 1,
  maxWidth: "100%",
  minHeight: 360,
  minWidth: 0,
  overflow: "hidden",
}));

const weekGridColumns = `56px repeat(${CALENDAR_WEEK_DAY_COUNT}, minmax(0, 1fr))`;

const WeekdayHeader = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "grid",
  gap: 0,
  gridTemplateColumns: weekGridColumns,
  minWidth: 0,
}));

const TimeGridBody = styled(Box)(() => ({
  display: "grid",
  gap: 0,
  gridTemplateColumns: weekGridColumns,
  minWidth: 0,
  position: "relative",
}));

const DayColumnHeader = styled(Box)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  textAlign: "center",
}));

const DayTimeArea = styled(Box)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  position: "relative",
}));

const HourRow = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxSizing: "border-box",
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  paddingRight: theme.spacing(0.5),
  textAlign: "right",
}));

function navigateDate(view: CalendarView, selectedDate: DateTime, delta: -1 | 1): DateTime {
  if (view === "day") {
    return selectedDate.plus({ days: delta });
  }
  if (view === "week") {
    return selectedDate.plus({ weeks: delta });
  }
  return selectedDate.plus({ months: delta });
}

/** Large title above the grid (e.g. day view: "WED APRIL 8"). */
function formatMainCalendarHeading(view: CalendarView, selectedDate: DateTime): string {
  if (view === "day") {
    return `${selectedDate.toFormat("ccc").toUpperCase()} ${selectedDate.toFormat("LLLL").toUpperCase()} ${selectedDate.day}`;
  }
  if (view === "week") {
    const days = getCalendarWeekDaysSunSat(selectedDate);
    const sun = days[0];
    const sat = days[6];
    if (!sun || !sat) {
      return "";
    }
    if (sun.month === sat.month) {
      return `${sun.toFormat("LLLL").toUpperCase()} ${sun.day}–${sat.day}, ${sun.year}`;
    }
    return `${sun.toFormat("LLL d").toUpperCase()} – ${sat.toFormat("LLL d, yyyy").toUpperCase()}`;
  }
  return `${selectedDate.toFormat("LLLL").toUpperCase()} ${selectedDate.year}`;
}

export const TaskCalendarTemplate = ({
  view,
  onViewChange,
  selectedDate,
  onSelectedDateChange,
  tasks,
  onOpenProspectFromTask,
}: TaskCalendarTemplateProps) => {
  const { normalizeEnabled, schedulePrefs, setNormalizeEnabled, setSchedulePrefs } = useTaskCalendarSchedule();
  const [detailTask, setDetailTask] = useState<CarouselTaskItem | null>(null);
  const [workHoursOpen, setWorkHoursOpen] = useState(false);
  const [draftSchedule, setDraftSchedule] = useState<TaskCalendarSchedulePrefs>(() => schedulePrefs);
  const [scheduleDayTab, setScheduleDayTab] = useState(0);
  const [now, setNow] = useState(() => DateTime.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(DateTime.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (workHoursOpen) {
      setDraftSchedule(schedulePrefs);
      setScheduleDayTab(0);
    }
  }, [workHoursOpen, schedulePrefs]);

  const today = DateTime.now().startOf("day");

  const displayDueMap = useMemo(() => {
    if (!normalizeEnabled) {
      return null;
    }
    return buildNormalizedDisplayDueByTaskId(tasks, schedulePrefs, true, now);
  }, [normalizeEnabled, now, schedulePrefs, tasks]);

  const tasksByDate = useMemo(() => tasksByLocalDateKey(tasks, displayDueMap), [tasks, displayDueMap]);
  const undated = useMemo(() => getUndatedTasks(tasks), [tasks]);

  const weekDays = useMemo(() => getCalendarWeekDaysSunSat(selectedDate), [selectedDate]);

  const monthCells = useMemo(() => getMonthGridCells(selectedDate), [selectedDate]);

  const allDayHours = useMemo(() => hoursInDay(), []);

  const daysForHourRowLayout = useMemo(() => {
    if (view === "week") {
      return weekDays.map((d) => d.startOf("day"));
    }
    return [selectedDate.startOf("day")];
  }, [view, weekDays, selectedDate]);

  const hourRowHeightsPx = useMemo(
    () => getHourRowHeightsPx(daysForHourRowLayout, schedulePrefs, HOUR_ROW_PX),
    [daysForHourRowLayout, schedulePrefs],
  );

  const dayGridTotalPx = totalDayGridHeightPx(hourRowHeightsPx);

  const timezoneLegendItems = useMemo(() => buildTimezoneLegendItems(tasks), [tasks]);
  const calendarMainHeadingPaddingTop = timezoneLegendItems.length > 0 ? 1.5 : 2;

  const handleViewSelect = (e: SelectChangeEvent<CalendarView>) => {
    onViewChange(e.target.value as CalendarView);
  };

  const goToday = () => onSelectedDateChange(DateTime.now().startOf("day"));

  const handleTaskClick = (task: CarouselTaskItem) => {
    if (task.prospectId && onOpenProspectFromTask) {
      onOpenProspectFromTask(task);
      return;
    }
    setDetailTask(task);
  };

  const renderNowLine = (day: DateTime, rowHeights: number[]) => {
    const dayStart = day.startOf("day");
    if (!now.hasSame(dayStart, "day")) {
      return null;
    }
    const minutes = now.hour * 60 + now.minute + now.second / 60;
    const total = totalDayGridHeightPx(rowHeights);
    const topPct = total > 0 ? (minuteOfDayToYpx(rowHeights, minutes) / total) * 100 : 0;
    return (
      <Box
        aria-hidden
        sx={(theme) => ({
          backgroundColor: theme.palette.error.main,
          height: 2,
          left: 0,
          pointerEvents: "none",
          position: "absolute",
          right: 0,
          top: `${topPct}%`,
          zIndex: 3,
        })}
      />
    );
  };

  const renderNonWorkingShading = (day: DateTime, rowHeights: number[]) =>
    getNonWorkingMinuteIntervals(day, schedulePrefs).map((seg, i) => {
      const { heightPct, topPct } = minuteSpanToPercentOfDay(rowHeights, seg.start, seg.end);
      return (
        <Box
          key={`nonwork-${day.toISODate()}-${i}`}
          aria-hidden
          sx={(theme) => ({
            backgroundColor: alpha(theme.palette.grey[500], 0.12),
            height: `${heightPct}%`,
            left: 0,
            pointerEvents: "none",
            position: "absolute",
            right: 0,
            top: `${topPct}%`,
            zIndex: 0,
          })}
        />
      );
    });

  const formatTaskTimeRange = (task: CarouselTaskItem): string => {
    const due = getCalendarDueForDisplay(task, displayDueMap);
    if (!due) {
      return "No due date";
    }
    const end = due.plus({ minutes: DEFAULT_TASK_DURATION_MINUTES });
    return `${due.toFormat("h:mm a")} – ${end.toFormat("h:mm a")}`;
  };

  const renderTimedPlacements = (
    day: DateTime,
    placements: ReturnType<typeof timedTasksForDay>,
    rowHeights: number[],
  ) => (
    <>
      {renderNowLine(day, rowHeights)}
      {placements.map((p) => {
        const { heightPct, topPct } = minuteSpanToPercentOfDay(rowHeights, p.startMin, p.endMin);
        const colWidthPct = 100 / p.colSpan;
        const leftPct = (p.col / p.colSpan) * 100;
        const accent = calendarAccentColorForTask(p.task);
        return (
          <Box
            key={p.task.id}
            component="button"
            type="button"
            onClick={() => handleTaskClick(p.task)}
            sx={(theme) => ({
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
              "&:hover": { filter: "brightness(0.97)" },
              backgroundColor: alpha(accent, 0.18),
              border: "none",
              borderLeft: `3px solid ${accent}`,
              borderRadius: `${Number(theme.shape.borderRadius)}px`,
              boxSizing: "border-box",
              color: theme.palette.text.primary,
              cursor: "pointer",
              height: `${heightPct}%`,
              left: `calc(${leftPct}% + 2px)`,
              overflow: "hidden",
              padding: theme.spacing(0.5, 0.75),
              position: "absolute",
              textAlign: "left",
              top: `${topPct}%`,
              width: `calc(${colWidthPct}% - 4px)`,
              zIndex: 1,
            })}
          >
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                gap: 0.5,
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Typography display="block" noWrap variant="caption" fontWeight={600}>
                  {p.task.title}
                </Typography>
                <Typography display="block" noWrap variant="caption" color="text.secondary">
                  {formatTaskTimeRange(p.task)}
                </Typography>
              </Box>
              {isUrgentOrHighCalendarPriority(p.task.priority) ? (
                <Flag
                  aria-label={calendarPriorityAriaLabel(p.task.priority)}
                  fontSize="small"
                  sx={(theme) => ({
                    color: calendarPriorityFlagColor(p.task.priority, theme),
                    flexShrink: 0,
                    mt: 0.125,
                  })}
                />
              ) : null}
            </Box>
          </Box>
        );
      })}
    </>
  );

  const dayWeekScrollSx = { maxHeight: "min(85vh, calc(100vh - 200px))", overflow: "auto", width: "100%" } as const;
  const weekTimeAreaHeight = "min(85vh, calc(100vh - 200px))";

  const dayView = () => {
    const day = selectedDate.startOf("day");
    const placements = timedTasksForDay(tasks, day, DEFAULT_TASK_DURATION_MINUTES, displayDueMap);
    return (
      <Box sx={dayWeekScrollSx}>
        <Box display="grid" gridTemplateColumns={`56px 1fr`} minWidth={0}>
          <Box>
            {allDayHours.map((h) => (
              <HourRow key={h} sx={{ height: hourRowHeightsPx[h] }}>
                {formatHourLabel(h)}
              </HourRow>
            ))}
          </Box>
          <DayTimeArea sx={{ height: dayGridTotalPx, minHeight: dayGridTotalPx }}>
            {renderNonWorkingShading(day, hourRowHeightsPx)}
            {renderTimedPlacements(day, placements, hourRowHeightsPx)}
          </DayTimeArea>
        </Box>
      </Box>
    );
  };

  const weekView = () => (
    <Box
      sx={{
        height: weekTimeAreaHeight,
        maxHeight: weekTimeAreaHeight,
        minHeight: 0,
        overflow: "auto",
        scrollbarGutter: "stable",
        width: "100%",
      }}
    >
      {/*
        Header and time grid share one scroll viewport so column widths match. A separate scroll on the grid only
        narrowed the body by the scrollbar width while the weekday row stayed full width, misaligning vertical rules.
      */}
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          position: "sticky",
          top: 0,
          zIndex: 2,
        })}
      >
        <Typography
          component="p"
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.04em",
            mb: 0,
            mt: 0,
            pt: calendarMainHeadingPaddingTop,
            px: 2,
            textAlign: "center",
          }}
        >
          {formatMainCalendarHeading("week", selectedDate)}
        </Typography>
        <WeekdayHeader>
          <Box />
          {weekDays.map((d) => (
            <DayColumnHeader key={d.toISODate()}>
              <Typography variant="caption" color="text.secondary" display="block">
                {d.toFormat("ccc").toUpperCase()}
              </Typography>
              <Typography variant="subtitle2" fontWeight={d.hasSame(today, "day") ? 700 : 500}>
                {d.day}
              </Typography>
            </DayColumnHeader>
          ))}
        </WeekdayHeader>
      </Box>
      <TimeGridBody>
        <Box>
          {allDayHours.map((h) => (
            <HourRow key={h} sx={{ height: hourRowHeightsPx[h] }}>
              {formatHourLabel(h)}
            </HourRow>
          ))}
        </Box>
        {weekDays.map((d) => (
          <DayTimeArea key={d.toISODate()} sx={{ height: dayGridTotalPx, minHeight: dayGridTotalPx }}>
            {renderNonWorkingShading(d, hourRowHeightsPx)}
            {renderTimedPlacements(
              d,
              timedTasksForDay(tasks, d, DEFAULT_TASK_DURATION_MINUTES, displayDueMap),
              hourRowHeightsPx,
            )}
          </DayTimeArea>
        ))}
      </TimeGridBody>
    </Box>
  );

  const monthView = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", minWidth: 0 }}>
      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
        <Typography
          key={label}
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ borderBottom: 1, borderColor: "divider", p: 1, textAlign: "center" }}
        >
          {label}
        </Typography>
      ))}
      {monthCells.map((cell) => {
        const iso = cell.toISODate() ?? "";
        const inMonth = cell.month === selectedDate.month;
        const dayTasks = tasksByDate.get(iso) ?? [];
        const timed = dayTasks.filter((t) => getCalendarDueForDisplay(t, displayDueMap));
        const maxShow = 3;
        const visible = timed.slice(0, maxShow);
        const extra = timed.length - visible.length;
        return (
          <Box
            key={iso}
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderRight: `1px solid ${theme.palette.divider}`,
              minHeight: 100,
              opacity: inMonth ? 1 : 0.45,
              p: 0.5,
            })}
          >
            <Button
              size="small"
              onClick={() => onSelectedDateChange(cell.startOf("day"))}
              sx={{
                mb: 0.5,
                minWidth: 0,
                p: 0.25,
                ...(cell.hasSame(today, "day")
                  ? {
                      "&:hover": { bgcolor: "primary.dark" },
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }
                  : {}),
              }}
            >
              <Typography variant="body2" fontWeight={cell.hasSame(selectedDate, "day") ? 700 : 400}>
                {cell.day}
              </Typography>
            </Button>
            <Stack spacing={0.25}>
              {visible.map((t) => {
                const due = getCalendarDueForDisplay(t, displayDueMap);
                return (
                  <Chip
                    key={t.id}
                    size="small"
                    label={
                      <Box component="span" sx={{ alignItems: "center", display: "flex", gap: 0.5, maxWidth: "100%" }}>
                        <Typography component="span" noWrap sx={{ flex: 1, minWidth: 0 }} variant="caption">
                          {due ? `${due.toFormat("h:mm a")} ` : ""}
                          {t.title}
                        </Typography>
                        {isUrgentOrHighCalendarPriority(t.priority) ? (
                          <Flag
                            aria-label={calendarPriorityAriaLabel(t.priority)}
                            component="span"
                            fontSize="inherit"
                            sx={(theme) => ({
                              color: calendarPriorityFlagColor(t.priority, theme),
                              flexShrink: 0,
                            })}
                          />
                        ) : null}
                      </Box>
                    }
                    onClick={() => handleTaskClick(t)}
                    sx={{
                      "& .MuiChip-label": { display: "block", overflow: "hidden", px: 0.75, textAlign: "left" },
                      backgroundColor: alpha(calendarAccentColorForTask(t), 0.2),
                      borderLeft: `3px solid ${calendarAccentColorForTask(t)}`,
                      height: "auto",
                      maxWidth: "100%",
                      py: 0.25,
                    }}
                  />
                );
              })}
              {extra > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  +{extra} more
                </Typography>
              ) : null}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Shell>
      <Stack spacing={2} sx={{ maxWidth: "100%" }}>
        <PageHeader
          title="Task Calendar"
          icon={<CalendarMonthIcon data-variant="page-header" />}
          stackToolbarOnMobile
          rightContent={
            <Box
              sx={{
                alignItems: { sm: "center", xs: "stretch" },
                display: { sm: "grid", xs: "flex" },
                flex: { sm: 1 },
                flexDirection: { xs: "column" },
                gap: 1,
                gridTemplateColumns: { sm: "1fr auto 1fr" },
                minWidth: 0,
                width: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{
                  gridColumn: { sm: "2" },
                  justifySelf: { sm: "center" },
                  width: { sm: "auto", xs: "100%" },
                }}
              >
                <Button size="small" variant="outlined" onClick={goToday}>
                  Today
                </Button>
                <Button
                  size="small"
                  aria-label="Previous"
                  onClick={() => onSelectedDateChange(navigateDate(view, selectedDate, -1))}
                >
                  <ChevronLeftIcon />
                </Button>
                <Button
                  size="small"
                  aria-label="Next"
                  onClick={() => onSelectedDateChange(navigateDate(view, selectedDate, 1))}
                >
                  <ChevronRightIcon />
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{
                  gridColumn: { sm: "3" },
                  justifySelf: { sm: "end" },
                  minWidth: 0,
                  width: { sm: "auto", xs: "100%" },
                }}
              >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="task-cal-view-label">View</InputLabel>
                  <Select labelId="task-cal-view-label" label="View" value={view} onChange={handleViewSelect}>
                    <MenuItem value="day">Day</MenuItem>
                    <MenuItem value="week">Week</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant={normalizeEnabled ? "contained" : "outlined"}
                  onClick={() => setNormalizeEnabled(!normalizeEnabled)}
                >
                  Normalize
                </Button>
                <Button size="small" variant="outlined" onClick={() => setWorkHoursOpen(true)}>
                  Work hours
                </Button>
              </Stack>
            </Box>
          }
        />
        {normalizeEnabled ? (
          <Typography variant="body2" color="text.secondary">
            Only <strong>past-due</strong> tasks are re-spaced from <strong>now</strong> forward in 10-minute steps (by
            original due order). Tasks still due in the future stay at their scheduled times. Non-working hours are
            skipped (see Work hours).
          </Typography>
        ) : null}

        <Stack spacing={2} sx={{ maxWidth: "100%", minWidth: 0 }}>
          {undated.length > 0 ? (
            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                No due date
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                {undated.slice(0, 12).map((t) => {
                  const accent = calendarAccentColorForTask(t);
                  return (
                    <Chip
                      key={t.id}
                      size="small"
                      label={
                        <Box
                          component="span"
                          sx={{ alignItems: "center", display: "flex", gap: 0.5, maxWidth: "100%" }}
                        >
                          <Typography component="span" noWrap sx={{ flex: 1, minWidth: 0 }} variant="caption">
                            {t.title}
                          </Typography>
                          {isUrgentOrHighCalendarPriority(t.priority) ? (
                            <Flag
                              aria-label={calendarPriorityAriaLabel(t.priority)}
                              component="span"
                              fontSize="inherit"
                              sx={(theme) => ({
                                color: calendarPriorityFlagColor(t.priority, theme),
                                flexShrink: 0,
                              })}
                            />
                          ) : null}
                        </Box>
                      }
                      onClick={() => handleTaskClick(t)}
                      sx={{
                        borderLeft: `3px solid ${accent}`,
                        justifyContent: "flex-start",
                        maxWidth: "100%",
                        ...(t.prospectTimeZone ? { backgroundColor: alpha(accent, 0.12) } : {}),
                      }}
                    />
                  );
                })}
                {undated.length > 12 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                    +{undated.length - 12} more
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          ) : null}

          <CalendarShell elevation={0} variant="outlined" sx={{ flex: 1, maxWidth: "100%" }}>
            {timezoneLegendItems.length > 0 ? (
              <Stack
                alignItems="center"
                aria-label="Time zone colors"
                component="section"
                direction="row"
                flexWrap="wrap"
                gap={1.5}
                px={2}
                pt={2}
                spacing={0}
                useFlexGap
              >
                <Typography color="text.secondary" fontWeight={600} variant="caption">
                  Time zones
                </Typography>
                {timezoneLegendItems.map((item) => (
                  <Stack key={item.iana} alignItems="center" direction="row" gap={0.75} spacing={0}>
                    <Box
                      aria-hidden
                      sx={{
                        backgroundColor: item.color,
                        borderRadius: 0.5,
                        flexShrink: 0,
                        height: 12,
                        width: 12,
                      }}
                    />
                    <Typography variant="caption">{item.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
            {view !== "week" ? (
              <Typography
                component="p"
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  mb: 0,
                  mt: 0,
                  pt: calendarMainHeadingPaddingTop,
                  px: 2,
                  textAlign: "center",
                }}
              >
                {formatMainCalendarHeading(view, selectedDate)}
              </Typography>
            ) : null}
            {view === "day" ? <Box sx={{ p: 1, pt: 2 }}>{dayView()}</Box> : null}
            {view === "week" ? <Box sx={{ minHeight: 0, pb: 1, px: 1 }}>{weekView()}</Box> : null}
            {view === "month" ? <Box sx={{ p: 1, pt: 2 }}>{monthView()}</Box> : null}
          </CalendarShell>
        </Stack>
      </Stack>

      <Dialog open={!!detailTask} onClose={() => setDetailTask(null)} maxWidth="sm" fullWidth>
        {detailTask ? (
          <>
            <DialogTitle>{detailTask.title}</DialogTitle>
            <DialogContent>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {formatTaskTimeRange(detailTask)}
                </Typography>
                {(() => {
                  const rawDue = taskDueDateTime(detailTask);
                  const shownDue = getCalendarDueForDisplay(detailTask, displayDueMap);
                  if (!rawDue || !shownDue || rawDue.toMillis() === shownDue.toMillis()) {
                    return null;
                  }
                  return (
                    <Typography variant="caption" color="text.secondary">
                      Originally due {rawDue.toFormat("ccc, MMM d • h:mm a")}
                    </Typography>
                  );
                })()}
                {detailTask.prospectName ? (
                  <Typography variant="body2">
                    Prospect: <strong>{detailTask.prospectName}</strong>
                  </Typography>
                ) : null}
                {detailTask.description ? (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {detailTask.description}
                  </Typography>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailTask(null)}>Close</Button>
              {detailTask.prospectId ? (
                <Button
                  component={RouterLink}
                  rel="noopener noreferrer"
                  target="_blank"
                  to={Route.salesPortalProspectDetails({ prospectId: detailTask.prospectId })}
                  variant="contained"
                >
                  Open
                </Button>
              ) : null}
              <Button component={RouterLink} to={Route.taskDashboard()} variant="outlined">
                Task dashboard
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog open={workHoursOpen} onClose={() => setWorkHoursOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Working hours (normalization)</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pick a day of the week. Uncheck &quot;Working day&quot; for days off. Normalized task times only land
              during working minutes; lunch and outside hours are skipped. End time before start time on the same day
              means an overnight shift (evening through the next morning).
            </Typography>
            <Tabs
              onChange={(_, v: number) => setScheduleDayTab(v)}
              scrollButtons="auto"
              value={scheduleDayTab}
              variant="scrollable"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {TASK_CALENDAR_SCHEDULE_DAY_LABELS.map((label, i) => (
                <Tab key={label} label={label} value={i} />
              ))}
            </Tabs>
            {(() => {
              const dayDraft = draftSchedule.days[scheduleDayTab];
              const patchDay = (patch: Partial<DaySchedulePrefs>) => {
                setDraftSchedule((prev) => ({
                  days: prev.days.map((d, i) => (i === scheduleDayTab ? { ...d, ...patch } : d)),
                }));
              };
              const lunchEnabled = dayDraft.lunchStartMin < dayDraft.lunchEndMin;
              return (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dayDraft.workingDay}
                        onChange={(e) => {
                          const on = e.target.checked;
                          patchDay(on ? { workingDay: true } : { lunchEndMin: 0, lunchStartMin: 0, workingDay: false });
                        }}
                      />
                    }
                    label="Working day"
                  />
                  <TextField
                    disabled={!dayDraft.workingDay}
                    fullWidth
                    label="Workday starts"
                    onChange={(e) => {
                      const m = timeInputValueToMinutes(e.target.value);
                      if (m != null) {
                        patchDay({ workdayStartMin: m });
                      }
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="time"
                    value={minutesToTimeInputValue(dayDraft.workdayStartMin)}
                  />
                  <TextField
                    disabled={!dayDraft.workingDay}
                    fullWidth
                    label="Workday ends"
                    onChange={(e) => {
                      const m = timeInputValueToMinutes(e.target.value);
                      if (m != null) {
                        patchDay({ workdayEndMin: m });
                      }
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="time"
                    value={minutesToTimeInputValue(dayDraft.workdayEndMin)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lunchEnabled}
                        disabled={!dayDraft.workingDay}
                        onChange={(e) => {
                          if (e.target.checked) {
                            patchDay({ lunchEndMin: 13 * 60, lunchStartMin: 12 * 60 });
                          } else {
                            patchDay({ lunchEndMin: 0, lunchStartMin: 0 });
                          }
                        }}
                      />
                    }
                    label="Lunch break"
                  />
                  <TextField
                    disabled={!dayDraft.workingDay || !lunchEnabled}
                    fullWidth
                    label="Lunch starts"
                    onChange={(e) => {
                      const m = timeInputValueToMinutes(e.target.value);
                      if (m != null) {
                        patchDay({ lunchStartMin: m });
                      }
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="time"
                    value={minutesToTimeInputValue(dayDraft.lunchStartMin)}
                  />
                  <TextField
                    disabled={!dayDraft.workingDay || !lunchEnabled}
                    fullWidth
                    label="Lunch ends"
                    onChange={(e) => {
                      const m = timeInputValueToMinutes(e.target.value);
                      if (m != null) {
                        patchDay({ lunchEndMin: m });
                      }
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="time"
                    value={minutesToTimeInputValue(dayDraft.lunchEndMin)}
                  />
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkHoursOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setSchedulePrefs(sanitizeSchedulePrefs(draftSchedule));
              setWorkHoursOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Shell>
  );
};
